import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Text, Flex, useToast, Skeleton, Switch } from '@chakra-ui/react';
import FeatherIcon from '@ailibs/feather-react-ts';
import { PiTicket } from 'react-icons/pi';
import { PageContent } from 'src/components/PageContent';
import { SectionBox } from 'src/components/SectionBox';
import { ShortcutCard } from '../Home/components/ShortcutCard';
import CenteredSpinner from '@/components/CenterSpinner/CenteredSpinner';
import { breadcrumb } from './breadcrumb';
import { ApplicationComponentId } from '@/model/application-component';
import { useAppApplicationComponentArea, useEnableApplicationComponentMutation } from '@/services/queries/admin-config';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import { useTranslation } from 'react-i18next';

type Link = {
  label: string;
  url: string;
  icon: ReactNode;
};

export const ApplicationCalendar = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [isAppAreaActive, setIsAppAreaActive] = useState(false);

  const links: Array<Link> = [
    {
      label: t('pages.application_calendar.calendar_events'),
      url: '/application/calendar/events',
      icon: <PiTicket />,
    },
    {
      label: t('pages.application_calendar.calendar_week_schedule'),
      url: '/application/calendar/week-schedule',
      icon: <FeatherIcon name='clock' />,
    },
  ];

  const { data: appAreaData, isLoading: isLoadingAppArea } = useAppApplicationComponentArea(
    undefined,
    ApplicationComponentId.CALENDAR,
  );
  const { mutate: enableAppAreaMutation, isLoading: isLoadingEnableOrDisableAppArea } = useEnableApplicationComponentMutation();
  const [pendingAppAreaState, setPendingAppAreaState] = useState<boolean | null>(null);
  const [isDialogAppAreaState, setIsDialogAppAreaState] = useState(false);

  const cancelAppAreaChange = () => {
    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  const handleAppAreaChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    setPendingAppAreaState(isChecked);
    setIsDialogAppAreaState(true);
  };

  const confirmAppAreaChange = () => {
    if (pendingAppAreaState === null) return;

    const successKey = pendingAppAreaState ? 'area_toggled_enable' : 'area_toggled_disable';
    const errorKey = pendingAppAreaState ? 'area_toggle_failed_enable' : 'area_toggle_failed_disable';

    setIsAppAreaActive(pendingAppAreaState);

    enableAppAreaMutation(
      [
        { component_id: ApplicationComponentId.CALENDAR_MATCHES, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.CALENDAR_EVENTS, active: pendingAppAreaState },
        { component_id: ApplicationComponentId.CALENDAR_WEEK_SCHEDULE, active: pendingAppAreaState },
      ],
      {
        onSuccess: () => {
          toast({
            status: 'success',
            title: t(`pages.application_calendar.calendar_${successKey}`),
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: t(`pages.application_calendar.calendar_${errorKey}`),
          });

          setIsAppAreaActive(!pendingAppAreaState);
        },
      },
    );

    setIsDialogAppAreaState(false);
    setPendingAppAreaState(null);
  };

  useEffect(() => {
    if (!appAreaData) return;

    setIsAppAreaActive(appAreaData.is_active);
  }, [appAreaData]);

  return (
    <PageContent title={t('pages.application_calendar.calendar_title')} breadcrumbItems={breadcrumb}>
      <SectionBox bodyProps={{ gap: 'small' }}>
        <Flex alignItems='center' gap={4}>
          {isLoadingAppArea ? (
            <>
              <Skeleton w='130px' h='21px' />
              <Skeleton w='102px' h='21px' />
            </>
          ) : (
            <>
              <Text fontWeight='bold' color='brand.500'>
                {t('pages.application_calendar.calendar_title_text')}
              </Text>

              <Switch size='sm' isChecked={isAppAreaActive} onChange={handleAppAreaChange}>
                {isAppAreaActive
                  ? t('pages.application_calendar.calendar_enabled')
                  : t('pages.application_calendar.calendar_disabled')}
              </Switch>
            </>
          )}
        </Flex>

        <Flex gap='xxsmall' flexWrap='wrap'>
          {links?.map(link => (
            <Link key={link.url} to={link.url}>
              <ShortcutCard label={link.label} icon={link.icon} />
            </Link>
          ))}
        </Flex>
      </SectionBox>

      <ConfirmationModal
        isOpen={isDialogAppAreaState}
        onClose={cancelAppAreaChange}
        onConfirm={confirmAppAreaChange}
        title={t('pages.application_calendar.calendar_title_text')}
        description={
          <Text>
            {pendingAppAreaState
              ? t('pages.application_calendar.calendar_description_enabled')
              : t('pages.application_calendar.calendar_description_disabled')}
          </Text>
        }
      />

      <CenteredSpinner isLoading={isLoadingEnableOrDisableAppArea} />
    </PageContent>
  );
};
